import { useEffect, useState } from "react";
import { useSubscriber } from "../../../../hooks";

const BASE_URL = `${process.env.REACT_APP_SERVER_URL}/peertalk`;
// const BASE_URL = `http://localhost:3000/peertalk`;

const usePeerTalk = () => {
  const [peerTalkData, setPeerTalkData] = useState([]);
  const [comments, setComments] = useState({});
  const [loadingComments, setLoadingComments] = useState(false);
  const [processingComment, setProcessingComment] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [
    isInappropriateCommentModalVisible,
    setIsInappropriateCommentModalVisible,
  ] = useState(false);
  const { fetchUserProfile, userDetails } = useSubscriber();
  const [activeStory, setActiveStory] = useState();
  const token = JSON.parse(localStorage.getItem("token"));

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Token ${token}`,
  };

  useEffect(() => {
    fetchUserProfile();
  }, []);

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const response = await fetch(`${BASE_URL}/stories`, {
          headers,
        });
        if (!response.ok) throw new Error("Failed to fetch stories");
        const data = await response.json();
        setPeerTalkData(data);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStories();
  }, []);

  const handleLike = async (storyId, commentId) => {
    try {
      // Optimistically update the state first
      setComments((prevComments) => {
        const updatedComments = (prevComments[storyId] || []).map((comment) =>
          comment._id === commentId
            ? {
                ...comment,
                likes: [...(comment.likes || []), userDetails.msisdn],
              }
            : comment
        );
        return { ...prevComments, [storyId]: updatedComments };
      });

      // Send the request to the server
      const response = await fetch(`${BASE_URL}/comments/${commentId}/like`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ userId: userDetails.msisdn }), // Send user ID in the request body
      });

      if (response.ok) {
        const updatedComment = await response.json();
        // Update the comment with the response from the server
        setComments((prevComments) => {
          const updatedComments = (prevComments[storyId] || []).map((comment) =>
            comment._id === commentId ? updatedComment : comment
          );
          return { ...prevComments, [storyId]: updatedComments };
        });
      } else {
        console.error("Failed to like/unlike comment");
        // Revert the optimistic update if the request fails
        setComments((prevComments) => {
          const revertedComments = (prevComments[storyId] || []).map(
            (comment) =>
              comment._id === commentId
                ? {
                    ...comment,
                    likes: (comment.likes || []).filter(
                      (id) => id !== userDetails.msisdn
                    ), // Remove the user ID from likes
                  }
                : comment
          );
          return { ...prevComments, [storyId]: revertedComments };
        });
      }
    } catch (error) {
      console.error("Error:", error);
      // Optionally revert the optimistic update here as well
      setComments((prevComments) => {
        const revertedComments = (prevComments[storyId] || []).map((comment) =>
          comment._id === commentId
            ? {
                ...comment,
                likes: (comment.likes || []).filter(
                  (id) => id !== userDetails.msisdn
                ),
              }
            : comment
        );
        return { ...prevComments, [storyId]: revertedComments };
      });
    }
  };

  const getStory = async (storyId) => {
    try {
      const response = await fetch(`${BASE_URL}/stories/${storyId}`, {
        method: "GET",
        headers,
      });

      const data = await response.json();
      setActiveStory(data);
    } catch (error) {
      console.error("Error fetching story", error);
    }
  };

  // New function to handle comment submission
  const submitComment = async (storyId, userId, inputComment) => {
    const newComment = {
      storyId: storyId,
      userId: userId,
      content: inputComment,
      date: new Date(),
      likes: [],
      flagged: false,
      reported: false,
      pinned: false,
    };
    setProcessingComment(true);
    try {
      const response = await fetch(`${BASE_URL}/comments`, {
        method: "POST",
        headers,
        body: JSON.stringify(newComment),
      });

      if (!response.ok) {
        if (response.status === 403) {
          setIsInappropriateCommentModalVisible(true);
        }
        throw new Error("Failed to submit comment");
      }

      const savedComment = await response.json();
      setComments((prevComments) => {
        // Update the comments for the specific story
        return {
          ...prevComments,
          [storyId]: [...(prevComments[storyId] || []), savedComment],
        };
      });
      await getStory(storyId);
      return savedComment;
    } catch (error) {
      console.log("Rethrown error:", error);
      throw new Error(`Error submitting comment: ${error.message}`);
    } finally {
      setProcessingComment(false);
    }
  };

  const handlePin = async (commentId, storyId) => {
    try {
      const response = await fetch(`${BASE_URL}/comments/${commentId}/pin`, {
        method: "PATCH",
        headers,
      });

      if (!response.ok) throw new Error("Failed to pin comment");

      // Update the comments state to reflect the pinned status
      setComments((prevComments) => {
        const updatedComments = (prevComments[storyId] || []).map((comment) => {
          if (comment._id === commentId) {
            return { ...comment, pinned: true }; // Mark as pinned
          }
          return comment;
        });
        return { ...prevComments, [storyId]: updatedComments };
      });
    } catch (error) {
      console.error("Error pinning comment:", error);
    }
  };

  const handleReport = async (commentId, storyId, userId) => {
    try {
      // Optimistically update the state first by toggling the report status
      setComments((prevComments) => {
        const updatedComments = (prevComments[storyId] || []).map((comment) => {
          if (comment._id === commentId) {
            // Toggle the reported status
            const reportedIndex = comment.reported.indexOf(userId);
            const newReported = [...comment.reported];

            if (reportedIndex === -1) {
              // User is not in the reported list, add them
              newReported.push(userId);
            } else {
              // User is in the reported list, remove them
              newReported.splice(reportedIndex, 1);
            }

            return { ...comment, reported: newReported };
          }
          return comment;
        });
        return { ...prevComments, [storyId]: updatedComments };
      });

      // Send the request to the server to report/unreport the comment
      const response = await fetch(`${BASE_URL}/comments/${commentId}/report`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ userId }), // Include userId in the request body
      });

      if (response.ok) {
        const updatedComment = await response.json();
        // Update the comment with the response from the server
        setComments((prevComments) => {
          const updatedComments = (prevComments[storyId] || []).map((comment) =>
            comment._id === commentId ? updatedComment : comment
          );
          return { ...prevComments, [storyId]: updatedComments };
        });
      } else {
        console.error("Failed to report/unreport comment");
        // Optionally revert the optimistic update if the request fails
        setComments((prevComments) => {
          const revertedComments = (prevComments[storyId] || []).map(
            (comment) =>
              comment._id === commentId
                ? {
                    ...comment,
                    reported: comment.reported.filter((id) => id !== userId), // Revert the reporting action
                  }
                : comment
          );
          return { ...prevComments, [storyId]: revertedComments };
        });
      }
    } catch (error) {
      console.error("Error reporting comment:", error);
      // Optionally revert the optimistic update here as well
      setComments((prevComments) => {
        const revertedComments = (prevComments[storyId] || []).map((comment) =>
          comment._id === commentId
            ? {
                ...comment,
                reported: comment.reported.filter((id) => id !== userId), // Revert the reporting action
              }
            : comment
        );
        return { ...prevComments, [storyId]: revertedComments };
      });
    }
  };

  const handleDelete = async (commentId, storyId) => {
    try {
      const response = await fetch(`${BASE_URL}/comments/${commentId}`, {
        method: "DELETE",
        headers,
      });

      if (!response.ok) throw new Error("Failed to delete comment");

      await getStory(storyId);
      // Update the comments state to remove the deleted comment
      setComments((prevComments) => {
        const updatedComments = (prevComments[storyId] || []).filter(
          (comment) => comment._id !== commentId
        );
        return { ...prevComments, [storyId]: updatedComments };
      });
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  const getCommentsByStoryId = async (storyId) => {
    try {
      setLoadingComments(true);
      const response = await fetch(`${BASE_URL}/comments/story/${storyId}`, {
        method: "GET",
        headers,
      });

      if (!response.ok) throw new Error("Failed to delete comment");
      const data = await response.json();
      setComments((prevComments) => {
        return { ...prevComments, [storyId]: data };
      });
    } catch (error) {
      console.error("Error deleting comment:", error);
    } finally {
      setLoadingComments(false);
    }
  };

  const closeInapproprateModal = () => {
    setIsInappropriateCommentModalVisible(false);
  };

  return {
    peerTalkData,
    comments,
    loading,
    error,
    submitComment,
    handleLike,
    userDetails,
    handleDelete,
    handlePin,
    handleReport,
    BASE_URL,
    getCommentsByStoryId,
    loadingComments,
    processingComment,
    getStory,
    activeStory,
    headers,
    isInappropriateCommentModalVisible,
    closeInapproprateModal,
  };
};

export default usePeerTalk;
